import { ChevronRightIcon, DatabaseIcon } from 'lucide-react'
import { type ReactNode, useMemo, useRef, useState } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  columnsFrom,
  formatCell,
  isElementId,
  isNumericColumn,
  type ParsedResult,
  type RunCypherArgs,
  shortId,
} from '@/features/agent/tools/run-cypher'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { cn } from '@/utils/cn'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface RunCypherViewProps {
  args: RunCypherArgs
  parsed: ParsedResult | null
  running: boolean
  errored: boolean
  /** Resolved action footer, supplied by the connected tool. Pure stories
   *  pass a mock `<ToolCardActions>` here to render the footer. */
  actions?: ReactNode
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

/** Pure presentation for a `run_cypher` tool-result card. */
export function RunCypherView({
  args,
  parsed,
  running,
  errored,
  actions,
  defaultOpen = false,
}: RunCypherViewProps) {
  const [showQuery, setShowQuery] = useState(false)

  const hasBody = !running && parsed != null
  const { open, onToggle } = useToolCardOpen(
    hasBody,
    defaultOpen,
    errored || parsed?.kind === 'error'
  )
  const headerStatus: ToolCallStatus = running ? 'running' : errored ? 'error' : 'complete'
  const description = running
    ? 'Running query'
    : parsed?.kind === 'rows'
      ? `${parsed.count} row${parsed.count === 1 ? '' : 's'}`
      : parsed?.kind === 'empty'
        ? '0 rows'
        : parsed?.kind === 'error'
          ? 'Failed'
          : null
  const durationMs =
    parsed?.kind === 'rows' || parsed?.kind === 'empty' ? parsed.elapsedMs : undefined

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={DatabaseIcon}
        toolName="run_cypher"
        description={description}
        durationMs={durationMs}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />

      {open && (
        <div className="flex flex-col gap-2 border-t border-border px-3 py-2">
          {parsed?.kind === 'rows' && <ResultTable rows={parsed.rows} />}
          {parsed?.kind === 'empty' && (
            <p className="text-xs text-muted-foreground">No rows returned.</p>
          )}
          {parsed?.kind === 'error' && (
            <p className="whitespace-pre-wrap text-xs text-muted-foreground">{parsed.message}</p>
          )}
          {parsed?.kind === 'raw' && (
            <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-muted/30 p-2 font-mono text-[10px] text-muted-foreground">
              {parsed.text}
            </pre>
          )}
          {actions}
          <section>
            <button
              type="button"
              onClick={() => setShowQuery(s => !s)}
              className="flex items-center gap-1 text-[10px] font-semibold uppercase text-muted-foreground hover:text-foreground max-md:py-2 max-md:pr-3 max-md:text-[11px]"
            >
              <ChevronRightIcon
                className={cn('size-3 transition-transform', showQuery && 'rotate-90')}
              />
              Query
            </button>
            {showQuery && (
              <>
                <pre className="mt-1 overflow-x-auto rounded bg-muted/50 p-2 font-mono text-[11px] leading-relaxed">
                  {args.cypher}
                </pre>
                {args.parameters && Object.keys(args.parameters).length > 0 && (
                  <pre className="mt-1 overflow-x-auto rounded bg-muted/30 p-2 font-mono text-[10px] text-muted-foreground">
                    params: {JSON.stringify(args.parameters, null, 2)}
                  </pre>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </ToolCallCard>
  )
}

function ResultTable({ rows }: { rows: Record<string, unknown>[] }) {
  const columns = useMemo(() => columnsFrom(rows), [rows])
  const preview = rows.slice(0, 50)
  const truncated = rows.length > preview.length
  const numericCols = useMemo(
    () => new Set(columns.filter(c => isNumericColumn(rows, c))),
    [columns, rows]
  )

  return (
    <div className="overflow-hidden rounded border border-border">
      <div className="max-h-[320px] overflow-auto">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr>
              {columns.map(c => (
                <th
                  key={c}
                  className={cn(
                    'px-2 py-1 font-medium text-muted-foreground',
                    numericCols.has(c) ? 'text-right' : 'text-left'
                  )}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.map((row, i) => (
              <tr key={i} className="border-t border-border">
                {columns.map(c => {
                  const raw = row[c]
                  const cellIsId = isElementId(raw)
                  const isNumeric = numericCols.has(c)
                  const display = cellIsId ? shortId(raw as string) : formatCell(raw)
                  const fullText =
                    raw === null || raw === undefined
                      ? ''
                      : typeof raw === 'object'
                        ? JSON.stringify(raw)
                        : String(raw)
                  const shortened = fullText.length > 0 && (cellIsId || fullText !== display)
                  const clipped = !shortened && fullText.length > CLIPPED_CELL_CHARS
                  const content = cellIsId ? (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-primary">
                      {display}
                    </span>
                  ) : (
                    display
                  )
                  return (
                    <td
                      key={c}
                      className={cn(
                        'max-w-[220px] truncate px-2 py-1 font-mono',
                        isNumeric && 'text-right tabular-nums'
                      )}
                    >
                      {shortened || clipped ? (
                        <FullValueTooltip fullText={fullText} touchOnly={clipped}>
                          {content}
                        </FullValueTooltip>
                      ) : (
                        content
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {truncated && (
        <p className="border-t border-border px-2 py-1 text-[10px] text-muted-foreground">
          Showing first {preview.length} of {rows.length} rows.
        </p>
      )}
    </div>
  )
}

/**
 * A plain value longer than this cannot fit the 220 px column (about 30
 * monospace characters at 11 px) and is cut by the cell's `truncate`.
 */
const CLIPPED_CELL_CHARS = 24

interface FullValueTooltipProps {
  fullText: string
  /** Open on tap only, never on hover: for values the column merely clips. */
  touchOnly?: boolean
  children: ReactNode
}

/**
 * The full value of a cell that shows less than it holds: shortened (element
 * id, array, object) or clipped by the column. A mouse gets the hover tooltip
 * on shortened values. Touch has no hover, so a tap pins the tooltip open; an
 * outside tap or a scroll closes it.
 */
function FullValueTooltip({ fullText, touchOnly = false, children }: FullValueTooltipProps) {
  const [open, setOpen] = useState(false)
  const lastPointerWasTouch = useRef(false)

  return (
    <Tooltip open={open} onOpenChange={next => setOpen(next && !touchOnly)}>
      <TooltipTrigger asChild>
        <span
          // Phone: the tap area grows into the cell's own padding. A clipped
          // value is cut by the span itself, so the tooltip anchors to the
          // visible part instead of the full-length text.
          className={
            touchOnly
              ? 'max-md:-my-1 max-md:block max-md:truncate max-md:py-1'
              : 'cursor-default max-md:relative max-md:after:absolute max-md:after:-inset-x-2 max-md:after:-inset-y-1'
          }
          onPointerDown={e => {
            lastPointerWasTouch.current = e.pointerType === 'touch'
          }}
          onClick={e => {
            if (!lastPointerWasTouch.current) return
            // Keeps the trigger's own click handler from closing it again.
            e.preventDefault()
            setOpen(true)
          }}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[calc(100vw-2rem)] break-all font-mono text-[11px] md:max-w-[480px]">
        {fullText}
      </TooltipContent>
    </Tooltip>
  )
}
